import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  CallRoute,
  CallRouteUsage,
  OtherCallRouteUsage,
  OverrideUsage,
  PhoneNumber,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { Department } from '@weave/schema-gen-ts/dist/schemas/phone-exp/departments/v2/department.pb';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Modal, ModalControlModalProps, useAlert, Text, TextLink } from '@frontend/design-system';
import { invalidateListOverrideQueries } from '../../../query-keys';
import { trackingId } from '../../../tracking';
import { logError } from '../helpers/utils';
import { ReassignPhoneNumber, SuccessMessageDisplay, UNASSIGNED_CALL_ROUTE_ID } from './reassign-phone-number-modal';

export const DeleteCallRouteModal = ({
  deleteModalProps,
  callRouteId,
  callRouteName,
  phoneNumbers,
  callRoutes,
  department,
  onSuccess,
}: {
  deleteModalProps: ModalControlModalProps;
  callRouteId?: string;
  callRouteName?: string;
  phoneNumbers: PhoneNumber[];
  callRoutes?: CallRoute[];
  department?: Department;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { mutateAsync: deleteCallRoute } = CallRouteV1.Mutations.useDeleteMutation({});
  const queryClient = useQueryClient();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const { data: callRoutesUsage = { usages: [] }, isLoading: callRoutesUsageIsLoading } =
    CallRouteV1.Queries.useUsageV2Query({
      request: { callRouteId: callRouteId ?? '' },
      options: {
        enabled: !!callRouteId,
        staleTime: 0,
      },
    });

  const [remainingPhoneNumbers, setRemainingPhoneNumbers] = useState<PhoneNumber[]>(phoneNumbers);

  const { mutate: updateCallRoutePhoneNumbers, isLoading: updateCallRoutePhoneNumbersIsLoading } =
    CallRouteV1.Mutations.useUpdatePhoneNumbersMutation();

  const currentPhoneNumber = remainingPhoneNumbers[0];

  const callRouteOptions = useMemo(() => {
    // Filter out the call route being deleted from the list of call routes
    // so that it doesn't show up in the dropdown.
    return (
      callRoutes?.filter((cr) => cr.callRouteId !== callRouteId).sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [callRoutes, callRouteId]);

  const { navigate: settingsNavigate } = useSettingsNavigate();

  const handleReassignPhoneNumber = (newCallRouteId: string, reassignAll: boolean) => {
    let phoneNumberIds: string[];

    let reassignedPhoneNumbers: PhoneNumber[] = [];

    if (newCallRouteId === UNASSIGNED_CALL_ROUTE_ID) {
      if (!callRouteId) {
        logError('No call route ID provided for unassignment', 'DeleteCallRouteModal:handleReassignPhoneNumber', {
          callRouteId,
          reassignAll,
        });
        alerts.error(t('No Call Route ID provided for unassignment.'));
        return;
      }

      // If unassigning, we need to remove the phone numbers from the call route
      if (reassignAll) {
        phoneNumberIds = []; // If reassigning all, we will remove all phone numbers from the call route
        reassignedPhoneNumbers = remainingPhoneNumbers; // If reassigning all, we will remove all phone numbers
      } else {
        // If unassigning only the current phone number, we will remove it from the call route
        phoneNumberIds = remainingPhoneNumbers.filter((num) => num.id !== currentPhoneNumber.id).map((num) => num.id);
        reassignedPhoneNumbers = [currentPhoneNumber]; // If reassigning only the current phone number
      }
      newCallRouteId = callRouteId;
    } else {
      // If reassigning to a new call route, we need to get the IDs of the phone numbers to reassign
      const newPhoneNumberIds = reassignAll ? remainingPhoneNumbers.map((num) => num.id) : [currentPhoneNumber.id];
      reassignedPhoneNumbers = reassignAll ? remainingPhoneNumbers : [currentPhoneNumber];
      const callRouteToReassign = callRoutes?.find((route) => route.callRouteId === newCallRouteId);

      if (!callRouteToReassign) {
        logError('No call route found for reassignment', 'DeleteCallRouteModal:handleReassignPhoneNumber', {
          newCallRouteId,
          reassignAll,
          callRouteIds: callRoutes?.map((route) => route.callRouteId),
        });
        alerts.error(t('No Call Route found for reassignment.'));
        return;
      }

      const callRoutePhoneNumberIds = callRouteToReassign.phoneNumberIds;
      phoneNumberIds = newPhoneNumberIds.concat(callRoutePhoneNumberIds); // Combine with existing phone numbers in the new call route
    }

    // Call API to reassign the phone number
    updateCallRoutePhoneNumbers(
      { phoneNumberIds, callRouteId: newCallRouteId },
      {
        onSuccess: () => {
          alerts.success({
            message: <SuccessMessageDisplay phoneNumbers={reassignedPhoneNumbers} />,
          });
          // Remove the phone number from the unassigned list
          if (reassignAll) {
            // If reassigning all, remove all remaining phone numbers
            setRemainingPhoneNumbers([]);
          } else {
            // If reassigning only the current phone number, remove just that one
            setRemainingPhoneNumbers((prev) => prev.filter((num) => num.id !== currentPhoneNumber.id));
          }
        },
        onError: () => {
          alerts.error(t('Failed to reassign Phone Number.'));
        },
      }
    );
  };

  const callRouteUsages = callRoutesUsage.usages.filter(
    (usage): usage is CallRouteUsage & { otherCallRouteUsage: OtherCallRouteUsage } => 'otherCallRouteUsage' in usage
  );
  const overrideUsages = callRoutesUsage.usages.filter(
    (usage): usage is CallRouteUsage & { overrideUsage: OverrideUsage } => 'overrideUsage' in usage
  );
  const hasCallRouteUsage = callRouteUsages.length > 0;
  const hasOverrideUsage = overrideUsages.length > 0;
  const hasDepartment = !!department;
  const hasRemainingNumbers = remainingPhoneNumbers.length > 0;
  const isReady = !callRoutesUsageIsLoading;

  return (
    <>
      {/* If there are no phone numbers assigned to the call route and there is no associated department, just
      display the confirmation modal */}
      {isReady && !hasCallRouteUsage && !hasOverrideUsage && !hasDepartment && !hasRemainingNumbers && (
        <ConfirmationModal
          {...deleteModalProps}
          maxWidth={600}
          modalCss={{ minWidth: '600px' }}
          title={t('Delete Call Route?')}
          message={
            <Trans t={t}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${theme.spacing(2)};
                `}
              >
                <Text>Deleting this route is permanent and may disrupt active workflows.</Text>

                {callRouteName && (
                  <Text>
                    Are you sure you want to delete<strong> {callRouteName}</strong>?
                  </Text>
                )}
              </div>
            </Trans>
          }
          cancelLabel={t('No, Cancel')}
          confirmLabel={t('Yes, Delete')}
          destructive
          cancelTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::cancel-btn',
          })}
          confirmTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::confirm-btn',
          })}
          onCancel={async () => {
            deleteModalProps.onClose();
          }}
          onConfirm={async () => {
            if (callRouteId) {
              deleteCallRoute({ callRouteId })
                .then(() => {
                  invalidateListOverrideQueries(queryClient, phoneTenantId);
                  alerts.success(t('Call Route deleted successfully.'));
                  deleteModalProps.onClose();
                  onSuccess?.();
                })
                .catch((error) => {
                  logError('Error deleting Call Route', 'DeleteCallRouteModal:onConfirm', {
                    callRouteId,
                    error,
                  });
                  alerts.error(t('Failed to delete Call Route.'));
                });
            }
          }}
        />
      )}

      {/* Callroute is used in Main Line Department and cannot be deleted */}
      {isReady && !hasCallRouteUsage && !hasOverrideUsage && hasDepartment && (
        <ConfirmationModal
          {...deleteModalProps}
          maxWidth={600}
          title={t('Cannot Delete Call Route')}
          subLabel={callRouteName}
          message={
            !!department.mainLine ? (
              <Text>
                {t('This call route cannot be deleted because it is related to your location’s Main Line Department.')}
              </Text>
            ) : (
              <Trans t={t}>
                In order to delete the {callRouteName} Call Route, the{' '}
                <TextLink
                  onClick={() => settingsNavigate({ to: '/phone/departments/:id', params: { id: department.id } })}
                >
                  {department.name} department
                </TextLink>{' '}
                must be deleted.
              </Trans>
            )
          }
          confirmLabel=''
          cancelLabel={t('Close')}
          onConfirm={() => {}}
          onCancel={deleteModalProps.onClose}
          cancelTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::department-cannot-delete-cancel-btn',
          })}
          show
          onClose={deleteModalProps.onClose}
        />
      )}

      {/* callroute has phone numbers assigned to it. They must be re-assigned before the callroute can be deleted */}
      {isReady && !hasCallRouteUsage && !hasOverrideUsage && !hasDepartment && hasRemainingNumbers && (
        <Modal {...deleteModalProps} minWidth={600}>
          <ReassignPhoneNumber
            remainingCount={phoneNumbers.length}
            currentPhoneNumber={currentPhoneNumber}
            callRouteName={callRouteName}
            remainingPhoneNumbers={remainingPhoneNumbers}
            callRoutes={callRouteOptions}
            isLoading={updateCallRoutePhoneNumbersIsLoading}
            onClose={deleteModalProps.onClose}
            handleReassignPhoneNumber={handleReassignPhoneNumber}
          />
        </Modal>
      )}

      {/* callroute is used in another call route and cannot be deleted */}
      {isReady && hasCallRouteUsage && (
        <ConfirmationModal
          {...deleteModalProps}
          maxWidth={600}
          title={t('Cannot Delete Call Route')}
          subLabel={callRouteName}
          message={
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(3),
              }}
            >
              <Text>
                {t(
                  `${callRouteName} can’t be deleted because it is currently in use in another Call Route. The item must be removed from these Call Routes in order to be deleted.`
                )}
              </Text>

              <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {callRouteUsages.map((usage) => (
                  <TextLink
                    key={usage.otherCallRouteUsage.callRouteId}
                    onClick={() =>
                      settingsNavigate({
                        to: '/phone/call-routes/:id',
                        params: { id: usage.otherCallRouteUsage.callRouteId },
                        search: { callObjectId: callRouteId },
                      })
                    }
                  >
                    {usage.otherCallRouteUsage.callRouteName}
                  </TextLink>
                ))}
              </div>
            </div>
          }
          confirmLabel=''
          cancelLabel={t('Close')}
          onConfirm={() => {}}
          onCancel={deleteModalProps.onClose}
          cancelTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::department-cannot-delete-cancel-btn',
          })}
          show
          onClose={deleteModalProps.onClose}
        />
      )}

      {/* callroute has phone tree(s) that are used in override(s) and cannot be deleted */}
      {isReady && !hasCallRouteUsage && hasOverrideUsage && (
        <ConfirmationModal
          {...deleteModalProps}
          maxWidth={600}
          title={t('Cannot Delete Call Route')}
          subLabel={callRouteName}
          message={
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--fabric-space-gap-xl)',
              }}
            >
              <Text>
                {t('One or more Phone Tree(s) in this Call Route is currently used in the following overrides.')}
              </Text>
              <Text>{t('Deleting this Call Route will cause these overrides to be removed.')}</Text>
              <Text>{t('The Phone Tree(s) must be removed from these Overrides in order to be deleted.')}</Text>

              <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {overrideUsages.map((usage) => (
                  <TextLink
                    key={usage.overrideUsage.overrideId}
                    onClick={() =>
                      settingsNavigate({
                        to: '/phone/overrides',
                        context: { overrideId: usage.overrideUsage.overrideId, openEditModal: true },
                      })
                    }
                  >
                    {usage.overrideUsage.name}
                  </TextLink>
                ))}
              </div>
            </div>
          }
          confirmLabel=''
          cancelLabel={t('Close')}
          onConfirm={() => {}}
          onCancel={deleteModalProps.onClose}
          cancelTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::override-usage-cannot-delete-cancel-btn',
          })}
          show
          onClose={deleteModalProps.onClose}
        />
      )}
    </>
  );
};
